/**
 * Super skills (the owner, 22/10/2026; docs/refactor/50): a skill used by a button, for people who are not technical.
 * The server's catalog says what each one asks (its menu) and which prompt it fills; this file turns the person's
 * choices into that prompt and into the plain words the prompt box shows instead of it.
 */

import type { SuperSkill, SuperSkillField } from './types'

export type SuperSkillValues = Record<string, string>

/** The longest a typed option may be: a filled prompt still fits a command of the queue. */
export const MAX_FIELD = 2000

const LANGUAGE: Record<string, string> = { 'pt-BR': 'Portuguese (Brazil)', 'en-US': 'English' }

/** The text in the person's language, or English, or whatever there is. */
export function local(text: Record<string, string> | null | undefined, locale: string): string {
  if (!text) return ''
  return text[locale] ?? text['en-US'] ?? Object.values(text)[0] ?? ''
}

/** Each option as the menu opens: its default, switches off unless said otherwise. */
export function initialValues(skill: SuperSkill): SuperSkillValues {
  return Object.fromEntries(skill.fields.map((f) => [f.key, f.default ?? (f.type === 'toggle' ? 'false' : '')]))
}

/** Whether an option shows now: always, or while the switch it hangs on is on ("newRepo") or the choice has that value. */
export function visible(field: SuperSkillField, values: SuperSkillValues): boolean {
  if (!field.when) return true
  const [other = '', value] = field.when.split('=')
  return value === undefined ? values[other] === 'true' : values[other] === value
}

/** The options still to fill before it can go: a required text left empty, a required switch left off. */
export function missing(skill: SuperSkill, values: SuperSkillValues): string[] {
  return skill.fields
    .filter((f) => f.required && visible(f, values))
    .filter((f) => (f.type === 'toggle' ? values[f.key] !== 'true' : !(values[f.key] ?? '').trim()))
    .map((f) => f.key)
}

/**
 * Whether the agent that would do it can. On the desktop the agent is known (the one chosen in Conectar agente, or
 * the @agent of this request); on the web the request goes to the computer at home, which knows its own.
 */
export function canRun(skill: SuperSkill, agent: string | null, desktop: boolean): boolean {
  if (!desktop || !skill.agents?.length) return true
  return agent !== null && skill.agents.includes(agent)
}

/** Whether its skill is on this computer: true/false on the desktop, null when nobody here can tell. */
export function installedHere(skill: SuperSkill, here: Array<{ name: string; enabled: boolean }> | null): boolean | null {
  if (!skill.skill || here === null) return null
  const names = skill.skill.detect.map((n) => n.toLowerCase())
  return here.some((s) => s.enabled && names.includes(s.name.toLowerCase()))
}

function valueFor(field: SuperSkillField | undefined, values: SuperSkillValues): string {
  if (!field || !visible(field, values)) return '(not given)'
  const value = (values[field.key] ?? '').trim().slice(0, MAX_FIELD)
  if (field.type === 'toggle') return value === 'true' ? 'yes' : 'no'
  return value || '(not given)'
}

/** The catalog prompt with the person's choices, their language and the skill's own line in its {{placeholders}}. */
export function fillPrompt(
  body: string,
  skill: SuperSkill,
  values: SuperSkillValues,
  context: { locale: string; installed: boolean | null; updateSkill: boolean },
): string {
  const filled: Record<string, string> = {
    language: LANGUAGE[context.locale] ?? context.locale,
    installed: context.installed === null ? 'unknown' : context.installed ? 'yes' : 'no',
    updateSkill: context.updateSkill ? 'yes' : 'no',
    skillName: skill.skill?.name ?? '',
    repo: skill.skill?.repo ?? '',
    install: skill.skill?.install ?? '',
  }
  return body.replace(/\{\{(\w+)\}\}/g, (all, key: string) => {
    const field = skill.fields.find((f) => f.key === key)
    if (field) return valueFor(field, values)
    return filled[key] ?? all
  })
}

/** What the prompt box and the reply show for it: "Criar um vídeo: promoção da padaria", each option cut short. */
export function shownText(skill: SuperSkill, values: SuperSkillValues, locale: string): string {
  return local(skill.shown, locale).replace(/\{\{(\w+)\}\}/g, (_, key: string) => {
    const field = skill.fields.find((f) => f.key === key)
    const value = (values[key] ?? '').trim()
    const label =
      field?.type === 'select' ? local(field.options?.find((o) => o.value === value)?.label, locale) || value
        // A file says its name, not the whole path.
        : field?.type === 'source' && !/^https?:\/\//i.test(value) ? value.split(/[\\/]/).pop() ?? value
          : value
    const line = label.replace(/\s+/g, ' ')
    return line.length > 60 ? `${line.slice(0, 59)}…` : line
  })
}
